/**
 * Cross-broker order blotter — phase 3 feature.
 *
 * Lists live + historical orders across every linked broker (Bamboo, CHD,
 * Meristem, Cordros). In production data is fetched via BrokerClient.orders()
 * with brokerId='*' — the BAL fans out concurrently and merges. In the offline
 * demo we seed a deterministic list so the surface is testable without a
 * backend.
 *
 * Rows surface:
 *   - placedAt, clOrdId, brokerOrderId, brokerId
 *   - symbol, securityType (EQUITY/BOND/ETF), side, qty, price, status
 *   - Cancel button (enabled only while status === WORKING)
 *   - Modify button (opens lightweight qty/price patch; uses broker.modifyOrder)
 *
 * Audit: every cancel/modify goes through the same idempotent BrokerClient
 * path and is written into the S3 Object-Lock audit bucket with the broker
 * that handled it, the user's Cognito sub, and the action metadata.
 */
import { useEffect, useMemo, useState } from 'react';
import { BROKERS, type BrokerId, brokerById } from '../lib/brokers';

type OrderStatus = 'WORKING' | 'FILLED' | 'PARTIAL' | 'CANCELLED' | 'REJECTED';

export interface BlotterRow {
  clOrdId: string;
  brokerOrderId: string;
  brokerId: BrokerId;
  accountId: string;
  symbol: string;
  securityType: 'EQUITY' | 'BOND' | 'ETF';
  side: 'BUY' | 'SELL';
  quantity: number;
  limitPriceKobo?: string;
  status: OrderStatus;
  placedAt: string;
  filledQty?: number;
  avgFillPriceKobo?: string;
  reason?: string;
}

interface Props {
  rows?: BlotterRow[];
  onCancel?: (r: BlotterRow) => Promise<void>;
  onModify?: (r: BlotterRow, patch: { quantity?: number; limitPriceKobo?: string }) => Promise<void>;
}

function seededRows(): BlotterRow[] {
  const now = Date.now();
  const base: Array<Partial<BlotterRow>> = [
    { clOrdId: 'clord-seed-1', brokerOrderId: 'BAM-991',  brokerId: 'bamboo',  accountId: 'acc-demo-1',
      symbol: 'ZENITHBANK', securityType: 'EQUITY', side: 'BUY',  quantity: 1000, limitPriceKobo: '442500', status: 'WORKING', placedAt: new Date(now - 1000 * 60 * 3).toISOString() },
    { clOrdId: 'clord-seed-2', brokerOrderId: 'CHD-7124', brokerId: 'chd',     accountId: 'acc-demo-1',
      symbol: 'FGN29-13.53', securityType: 'BOND',   side: 'BUY',  quantity: 5000, limitPriceKobo: '94200000', status: 'FILLED', placedAt: new Date(now - 1000 * 60 * 12).toISOString(), filledQty: 5000, avgFillPriceKobo: '94180000' },
    { clOrdId: 'clord-seed-3', brokerOrderId: 'MER-5512', brokerId: 'meristem',accountId: 'acc-demo-1',
      symbol: 'NEWGOLD',     securityType: 'ETF',    side: 'BUY',  quantity: 2,    limitPriceKobo: '5820000', status: 'PARTIAL', placedAt: new Date(now - 1000 * 60 * 35).toISOString(), filledQty: 1, avgFillPriceKobo: '5815000' },
    { clOrdId: 'clord-seed-4', brokerOrderId: 'BAM-990',  brokerId: 'bamboo',  accountId: 'acc-demo-1',
      symbol: 'DANGCEM',     securityType: 'EQUITY', side: 'SELL', quantity: 50,   limitPriceKobo: '68300', status: 'CANCELLED', placedAt: new Date(now - 1000 * 60 * 55).toISOString(), reason: 'User cancelled' },
    { clOrdId: 'clord-seed-5', brokerOrderId: 'COR-2014', brokerId: 'cordros', accountId: 'acc-demo-1',
      symbol: 'VETGRIF30',   securityType: 'ETF',    side: 'BUY',  quantity: 500,  limitPriceKobo: '3855', status: 'REJECTED', placedAt: new Date(now - 1000 * 60 * 90).toISOString(), reason: 'insufficient_funds' },
  ];
  return base as BlotterRow[];
}

export function OrderBlotter({ rows, onCancel, onModify }: Props = {}) {
  const [filter, setFilter] = useState<'ALL' | BrokerId>('ALL');
  const [secFilter, setSecFilter] = useState<'ALL' | 'EQUITY' | 'BOND' | 'ETF'>('ALL');
  const [editing, setEditing] = useState<BlotterRow | undefined>();
  const [editQty, setEditQty] = useState('');
  const [editPx, setEditPx] = useState('');
  const [busy, setBusy] = useState(false);
  const all = rows ?? useMemo(() => seededRows(), []);

  const filtered = useMemo(() => {
    let r = all;
    if (filter !== 'ALL') r = r.filter((x) => x.brokerId === filter);
    if (secFilter !== 'ALL') r = r.filter((x) => x.securityType === secFilter);
    return r;
  }, [all, filter, secFilter]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { ALL: all.length };
    for (const b of BROKERS) c[b.id] = all.filter((x) => x.brokerId === b.id).length;
    return c;
  }, [all]);

  const cancel = async (r: BlotterRow) => {
    if (!confirm(`Cancel order ${r.brokerOrderId} on ${brokerById(r.brokerId)?.label}?`)) return;
    setBusy(true);
    try { await onCancel?.(r); }
    finally { setBusy(false); }
  };
  const applyModify = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await onModify?.(editing, {
        quantity: editQty ? Number(editQty) : undefined,
        limitPriceKobo: editPx ? String(Math.round(Number(editPx) * 100)) : undefined,
      });
      setEditing(undefined);
    } finally { setBusy(false); }
  };

  return (
    <div className="blotter">
      <div className="blotter-head">
        <h2>Order Blotter</h2>
        <div className="blotter-filters">
          <button className={'bl-pill ' + (filter === 'ALL' ? 'active' : '')} onClick={() => setFilter('ALL')}>All ({counts.ALL})</button>
          {BROKERS.map((b) => (
            <button key={b.id} className={'bl-pill ' + (filter === b.id ? 'active' : '')} onClick={() => setFilter(b.id)}>
              {b.logoGlyph} {b.label} ({counts[b.id] ?? 0})
            </button>
          ))}
          <span className="bl-sep" />
          {(['ALL', 'EQUITY', 'BOND', 'ETF'] as const).map((s) => (
            <button key={s} className={'bl-pill ' + (secFilter === s ? 'active' : '')} onClick={() => setSecFilter(s)}>{s}</button>
          ))}
        </div>
      </div>
      <div className="blotter-table" role="table">
        <div className="bl-row bl-head" role="row">
          <div>Placed</div><div>Broker</div><div>Type</div><div>Symbol</div><div>Side</div>
          <div>Qty</div><div>Price</div><div>Status</div><div>Actions</div>
        </div>
        {filtered.map((r) => (
          <div key={r.clOrdId} className="bl-row" role="row">
            <div className="mono">{r.placedAt.slice(11, 19)}</div>
            <div><span className="bl-broker">{brokerById(r.brokerId)?.logoGlyph} {brokerById(r.brokerId)?.label}</span></div>
            <div><span className={'bl-sec ' + r.securityType.toLowerCase()}>{r.securityType}</span></div>
            <div><strong>{r.symbol}</strong></div>
            <div><span className={'bl-side ' + (r.side === 'BUY' ? 'buy' : 'sell')}>{r.side}</span></div>
            <div className="mono">{r.quantity.toLocaleString('en-NG')}{r.filledQty && r.filledQty < r.quantity ? ` (${r.filledQty})` : ''}</div>
            <div className="mono">{r.limitPriceKobo ? '\u20A6' + (Number(r.limitPriceKobo) / 100).toLocaleString('en-NG', { maximumFractionDigits: 2 }) : 'MKT'}</div>
            <div><span className={'bl-status bl-' + r.status.toLowerCase()}>{r.status}</span></div>
            <div className="bl-actions">
              {r.status === 'WORKING' && (
                <>
                  <button className="bl-btn" onClick={() => { setEditing(r); setEditQty(String(r.quantity)); setEditPx(r.limitPriceKobo ? String(Number(r.limitPriceKobo) / 100) : ''); }} disabled={busy}>Modify</button>
                  <button className="bl-btn bl-cancel" onClick={() => void cancel(r)} disabled={busy}>Cancel</button>
                </>
              )}
              {r.reason && <span className="bl-reason" title={r.reason}>{r.reason}</span>}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="bl-empty">No orders matching filters.</div>
        )}
      </div>

      {editing && (
        <div className="wl-modal-backdrop" role="dialog" aria-modal="true">
          <div className="wl-modal" style={{ maxWidth: 420 }}>
            <div className="wl-modal-h">
              <strong>Modify {editing.brokerOrderId}</strong>
              <button className="wl-close" onClick={() => setEditing(undefined)} aria-label="Close">&times;</button>
            </div>
            <div className="wl-new-body">
              <label className="wl-new-label">Quantity</label>
              <input className="wl-new-input" inputMode="numeric" value={editQty} onChange={(e) => setEditQty(e.target.value.replace(/[^\d]/g, ''))} />
              <label className="wl-new-label">Limit Price (\u20A6)</label>
              <input className="wl-new-input" inputMode="decimal" value={editPx} onChange={(e) => setEditPx(e.target.value)} />
            </div>
            <div className="wl-modal-foot">
              <button className="wl-cancel" onClick={() => setEditing(undefined)}>Close</button>
              <button className="wl-save" onClick={() => void applyModify()} disabled={busy}>{busy ? 'Modifying\u2026' : 'Apply Modify'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
